import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import dialerStoreData from "Stores/DialerData";
import DialerServiceData from "Stores/DialerServiceStore";
import DropDownMenu from "Components/common/DropDownMenu";
import TextArea from "Components/common/TextArea";
import Button from "Components/common/Button";
import CallsUtils from "Utils/CallsUtils";
import { makeApi, URLS } from "Utils/apiURL";
import { mapAiNotes } from "Utils/AiNotesUtils";

const CallLogPopup = observer(props => {
  const { isOpen, onClose, onLogCall, callSid = "" } = props || {};

  const [callOutcome, setCallOutcome] = useState({ value: "Select" });
  const [notes, setNotes] = useState("");
  const [showDropdownMenu, setDropdownMenu] = useState(false);
  const [err, setErr] = useState("");
  const [loader, setLoader] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCallOutcome({ value: "Select" });
      setNotes(DialerServiceData.dialerCallNotes || "");
      setErr("");
      fetchAiNotes();
    }
  }, [isOpen]);

  const fetchAiNotes = async () => {
    if (!callSid) return;
    setAiLoading(true);
    setAiData(null);
    try {
      const config = { url: `${URLS.generateAiCallNotes}?callSid=${callSid}`, method: "GET" };
      const res = await makeApi(config);
      const { data = {} } = res || {};
      if (data?.responseCode === 200 && data?.aiNotes?.aiNotes) {
        setAiData(mapAiNotes(data.aiNotes.aiNotes));
      }
    } catch (e) {
      // silent fail — don't block call logging
    } finally {
      setAiLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const getCallOutcome = (_, item = { value: "Select" }) => {
    err && setErr("");
    setCallOutcome(item);
    setDropdownMenu(false);
  };

  const handleLogCall = async () => {
    if (!callOutcome?.id) {
      setErr("Please select a call outcome");
      return;
    }
    setLoader(true);
    await onLogCall({
      callDispositionId: callOutcome.id,
      callOutcome: callOutcome.value,
      notes,
      callSid
    });
    setLoader(false);
  };

  const outcomeOptions = CallsUtils.getLogOutcomeDropdown(
    dialerStoreData?.stageList,
    getCallOutcome
  );

  const textAreaObj = {
    id: "callLogNotes",
    labelText: "Call Notes",
    value: notes,
    onChangeEvent: e => setNotes(e.currentTarget.value)
  };

  const logBtnObj = {
    id: "callLogSubmitBtn",
    name: "callLogSubmitBtn",
    btnText: "Log Call",
    icon: false,
    iconName: "",
    type: "button",
    className: `logCallBtn ${loader ? "loading" : ""}`,
    loader,
    click: handleLogCall
  };

  const visibleKeyPoints = aiData?.keyPoints?.slice(0, 3) || [];
  const firstNextStep = aiData?.nextSteps?.[0] || "";

  return (
    <>
      <div className="callLogPopup">
        <div className="callLogPopupCard animated customzoomIn">
          <div className="callLogPopupHeader">
            <div className="callLogPopupTitleSection">
              <span className="callLogPopupTitle">Call Summary</span>
              <span className="callLogPopupSubTitle">
                AI-generated notes from your call
              </span>
            </div>
            <i className="material-icons callLogPopupClose" onClick={onClose}>
              close
            </i>
          </div>

          <div className="callLogPopupBody">
            {aiLoading && (
              <div className="callLogAiSection callLogAiSection--loading">
                <div className="callLogAiSkeleton callLogAiSkeleton--summary" />
                <div className="callLogAiSkeleton callLogAiSkeleton--line" />
                <div className="callLogAiSkeleton callLogAiSkeleton--line" />
              </div>
            )}

            {!aiLoading && aiData && (
              <>
                {aiData.summary && (
                  <div className="callLogAiSection">
                    <div className="callLogAiBadge">
                      <span className="callLogAiBadgeText">AI Summary</span>
                    </div>
                    <p className="callLogAiSummary">{aiData.summary}</p>
                  </div>
                )}

                {visibleKeyPoints.length > 0 && (
                  <div className="callLogKeyPoints">
                    <p className="callLogSectionLabel">Key Points</p>
                    <div className="callLogKeyPointsList">
                      {visibleKeyPoints.map((point, idx) => (
                        <div key={idx} className="callLogKeyPointItem">
                          <span className={`callLogKeyPointTag tag-${point.cat.toLowerCase().replace(" ", "-")}`}>
                            {point.cat}
                          </span>
                          <span className="callLogKeyPointText">{point.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {firstNextStep && (
                  <div className="callLogNextSteps">
                    <p className="callLogSectionLabel">Suggested Next Step</p>
                    <ul className="callLogNextStepsList">
                      <li className="callLogNextStepItem">{firstNextStep}</li>
                    </ul>
                  </div>
                )}
              </>
            )}

            <div className="callLogOutcomeSection">
              <p className="callLogSectionLabel">Call Outcome</p>
              <div className="callOutcomeBtn" onClick={() => setDropdownMenu(true)}>
                <span
                  className="text"
                  style={{
                    opacity:
                      callOutcome?.value?.toLowerCase() === "select" ? 0.7 : 1
                  }}
                >
                  {callOutcome?.value}
                </span>
                <i className="material-icons">
                  {!showDropdownMenu ? "keyboard_arrow_down" : "keyboard_arrow_up"}
                </i>
                {err && <span className="errMsgText">{err}</span>}
              </div>
              {showDropdownMenu && (
                <>
                  <DropDownMenu options={outcomeOptions} direction="center" />
                  <div
                    className="overlayCallOutcomeBg"
                    onClick={() => setDropdownMenu(false)}
                  />
                </>
              )}
            </div>

            <div className="callNotesWrap">
              <TextArea {...textAreaObj} />
            </div>
          </div>

          <div className="callLogPopupFooter">
            <div className="logCallBtnWrapper">
              <Button {...logBtnObj} />
            </div>
          </div>
        </div>
      </div>
      <div className="callLogPopupOverlay" />
    </>
  );
});

export default CallLogPopup;
export { CallLogPopup };
